/**
 * @component HomePage
 * @description 메인 히어로, 핵심 솔루션 요약, 신뢰 지표를 통해 방문자에게 첫 메시지를 전달하는 랜딩 페이지입니다.
 * @usage App 라우팅에서 `/` 경로일 때 렌더링됩니다.
 */

import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { ChevronRight, FlaskConical, Headphones, Network, ShieldCheck } from 'lucide-react';
import SectionHeading from '../../components/common/SectionHeading';
import { adminDashboard, solutionMes, solutionWeigh, solutionWms } from '../../assets';
import { heroSlides, pharmaFlow, supportFlow, traceItems } from '../../constants/homeConstants';
import { solutionItems } from '../../constants/solutionData';

/** 솔루션 ID → 이미지 매핑 */
const solutionImages = {
  rws: solutionWeigh,
  mes: solutionMes,
  wms: solutionWms,
};

/** 히어로 슬라이더 이전 버튼 */
function CustomPrevArrow({ className, onClick }) {
  return (
    <button type="button" className={`${className || ''} hero-arrow hero-arrow-prev`} onClick={onClick} aria-label="Previous slide">
      <span aria-hidden="true">&#10094;</span>
    </button>
  );
}

/** 히어로 슬라이더 다음 버튼 */
function CustomNextArrow({ className, onClick }) {
  return (
    <button type="button" className={`${className || ''} hero-arrow hero-arrow-next`} onClick={onClick} aria-label="Next slide">
      <span aria-hidden="true">&#10095;</span>
    </button>
  );
}

export default function HomePage({ onNavigate }) {
  // [비즈니스] 방문 시점 기준 3일 단위로 초기 슬라이드를 순환 — 같은 기간 방문자는 동일한 슬라이드를 보게 됩니다.
  const initialSlide = Math.floor(Date.now() / (1000 * 60 * 60 * 24 * 3)) % heroSlides.length;

  // [슬라이더 설정]: 자동 재생, 전환 속도, 커스텀 화살표 UI, 날짜 기반 초기 슬라이드를 지정합니다.
  const settings = {
    dots: false,
    infinite: true,
    speed: 1200,
    autoplay: true,
    autoplaySpeed: 7500,
    arrows: true,
    initialSlide,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
  };

  const goToWithGmpModules = () => {
    onNavigate('/solution/withgmp.html');

    if (typeof window === 'undefined') return;

    window.setTimeout(() => {
      const el = document.getElementById('wgmp-sec-modules');
      if (el) window.scrollTo({ top: el.offsetTop - 40, behavior: 'smooth' });
    }, 80);
  };

  return (
    <>
      {/* ── 1. 메인 히어로 슬라이더 ── */}
      <section className="hero-section">
        <Slider className="hero-slider" {...settings}>
          {heroSlides.map((slide) => (
            <article key={slide.id} className="hero-slide">
              <img className="hero-slide-image" src={slide.image} alt="" aria-hidden="true" />
              <div className="hero-slide-overlay" aria-hidden="true" />
              <div className="container hero-slide-inner">
                <div className="hero-copy fade-in">
                  <p className="eyebrow">{slide.eyebrow}</p>
                  {/* [기능] 모든 슬라이드를 동일한 구조로 렌더링: 솔루션 문구 + 브랜드 마감 */}
                  <h1>
                    {slide.line1}
                    <br /> 새힘정보기술이 완성합니다.
                  </h1>
                  <p>{slide.description}</p>
                  <div className="hero-actions">
                    {slide.id === 'hero-main' ? (
                      /* [이벤트 처리]: 메인 슬라이드는 withGMP 모듈 섹션으로 스크롤 이동 */
                      <button type="button" className="primary-button" onClick={goToWithGmpModules}>
                        제품 더 알아보기
                      </button>
                    ) : (
                      /* [이벤트 처리]: 솔루션 슬라이드는 각 솔루션 상세 페이지로 이동 */
                      <button type="button" className="primary-button" onClick={() => onNavigate(slide.href)}>
                        {slide.ctaLabel}
                      </button>
                    )}
                    {/* [이벤트 처리]: 문의 페이지로 이동해 상담 유입을 유도합니다. */}
                    <button type="button" className="secondary-button" onClick={() => onNavigate('/contact')}>
                      문의하기
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </Slider>
      </section>

      {/* ── 2. Core Solutions 카드 그리드 ── */}
      <section className="section">
        <div className="container">
          <SectionHeading
            eyebrow="Core Solutions"
            title="현장에 맞춘 세 가지 핵심 솔루션"
            description="품질, 제조, 창고 운영을 연결하는 솔루션을 한 번에 확인해 보세요."
            className="fade-in"
          />

          <div className="solution-visual-grid">
            {solutionItems.map((item) => (
              <article key={item.id} className={`solution-visual-card solution-visual-card--${item.id} fade-in`}>
                <div className="solution-visual-bg" aria-hidden="true">
                  <img src={solutionImages[item.id]} alt="" />
                </div>
                <div className="solution-visual-text">
                  <span className="solution-visual-label">{item.badge}</span>
                  <h3>{item.title}</h3>
                  <p>{item.summary}</p>
                  {/* [이벤트 처리]: 해당 솔루션 상세 페이지로 이동합니다. */}
                  <button type="button" className="solution-visual-cta" onClick={() => onNavigate(item.href)}>
                    자세히 보기 →
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Why Saehim 섹션 ── */}
      <section className="why-saehim-section">
        <div className="container why-saehim-container">

          {/* 3-1. 카피 + 대시보드 이미지 */}
          <div className="why-saehim-hero">
            <div className="why-saehim-copy fade-in">
              <p className="why-eyebrow">WHY SAEHIM</p>
              <h2 className="why-title">
                현장의 데이터를
                <br />
                <span>운영 흐름으로 연결합니다</span>
              </h2>
              <p className="why-desc">
                제약·화장품 제조 현장의 원료 칭량, 생산, 창고 데이터를
                <br />
                하나의 흐름으로 연결해 추적 가능한 운영 체계를 만듭니다.
              </p>
            </div>

            <div className="why-saehim-visual fade-in">
              <div className="why-dash-wrap">
                <img src={adminDashboard} alt="RWS MES WMS 통합 운영 대시보드" className="why-dash-img" />
              </div>
            </div>
          </div>

          {/* 3-2. 특징 4-카드 그리드 */}
          <div className="why-feature-grid">

            {/* 제약·화장품 특화 카드 — 제조 프로세스 흐름 */}
            <article className="why-feature-card why-feature-card--pharma fade-in">
              <div className="why-feature-heading">
                <div className="why-feature-icon why-feature-icon--mint">
                  <FlaskConical size={38} strokeWidth={1.7} />
                </div>
                <div>
                  <h3>제약 · 화장품 제조에 맞춘 설계</h3>
                  <p>Pharma / Cosmetics</p>
                </div>
              </div>

              <div className="why-process-flow" aria-label="제약 화장품 제조 운영 흐름">
                {pharmaFlow.map(({ label, Icon }, index) => (
                  <div className="why-process-item" key={label}>
                    <div className="why-process-badge">
                      <Icon size={24} strokeWidth={1.7} />
                    </div>
                    <span>{label}</span>
                    {index < pharmaFlow.length - 1 ? <ChevronRight className="why-process-arrow" size={22} strokeWidth={1.8} aria-hidden="true" /> : null}
                  </div>
                ))}
              </div>
            </article>

            {/* RWS·MES·WMS 통합 카드 — 오빗(Orbit) 다이어그램 */}
            <article className="why-feature-card why-feature-card--integrated fade-in">
              <div className="why-feature-heading why-feature-heading--stacked">
                <div className="why-feature-icon why-feature-icon--blue">
                  <Network size={38} strokeWidth={1.7} />
                </div>
                <div>
                  <h3>RWS · MES · WMS 통합</h3>
                  <p>Weighing · Manufacturing · Warehouse</p>
                </div>
              </div>

              <div className="why-orbit" aria-label="통합 데이터 플랫폼 구성">
                <span className="why-orbit-card why-orbit-card--top">RWS<br /><em>(칭량)</em></span>
                <span className="why-orbit-card why-orbit-card--left">MES<br /><em>(제조)</em></span>
                <span className="why-orbit-card why-orbit-card--right">WMS<br /><em>(창고)</em></span>
                <span className="why-orbit-center">통합<br />데이터 플랫폼</span>
                <span className="why-orbit-dot why-orbit-dot--one" />
                <span className="why-orbit-dot why-orbit-dot--two" />
                <span className="why-orbit-dot why-orbit-dot--three" />
              </div>
            </article>

            {/* Lot 추적 카드 — 추적 항목 리스트 */}
            <article className="why-feature-card why-feature-card--trace fade-in">
              <div className="why-feature-heading why-feature-heading--stacked">
                <div className="why-feature-icon why-feature-icon--violet">
                  <ShieldCheck size={38} strokeWidth={1.7} />
                </div>
                <div>
                  <h3>Lot 추적 구조</h3>
                  <p>Traceability &amp; Standardization</p>
                </div>
              </div>

              <div className="why-trace-list">
                {traceItems.map(({ label, Icon }) => (
                  <div className="why-trace-row" key={label}>
                    <Icon size={21} strokeWidth={1.8} />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </article>

            {/* 구축 이후 운영 지원 카드 — 지원 프로세스 흐름 */}
            <article className="why-feature-card why-feature-card--support fade-in">
              <div className="why-feature-heading why-feature-heading--stacked">
                <div className="why-feature-icon why-feature-icon--cyan">
                  <Headphones size={38} strokeWidth={1.7} />
                </div>
                <div>
                  <h3>구축 이후 운영 지원</h3>
                  <p>Operation &amp; Maintenance</p>
                </div>
              </div>

              <div className="why-support-flow" aria-label="운영 지원 프로세스">
                {supportFlow.map(({ label, Icon }, index) => (
                  <div className="why-support-item" key={label}>
                    <div className="why-support-dot">
                      <Icon size={23} strokeWidth={1.7} />
                    </div>
                    <span>{label}</span>
                    {index < supportFlow.length - 1 ? <span className="why-support-line" aria-hidden="true" /> : null}
                  </div>
                ))}
              </div>
            </article>

          </div>
        </div>
      </section>
    </>
  );
}
